import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'sonner';
import { AuthProvider, useAuth } from './contexts/AuthContext';

// Pages
import LoginPage from './pages/LoginPage';
import ShiftsPage from './pages/ShiftsPage';
import ShiftSearchPage from './pages/ShiftSearchPage';
import CashupPage from './pages/CashupPage';
import LedgerPage from './pages/LedgerPage';
import ExpensesPage from './pages/ExpensesPage';
import CashCountPage from './pages/CashCountPage';
import ExportPage from './pages/ExportPage';
import UsersPage from './pages/UsersPage';
import ApiKeysPage from './pages/ApiKeysPage';
import APIUsersPage from './pages/APIUsersPage';
import AccountingSettingsPage from './pages/AccountingSettingsPage';
import OAuthCallbackPage from './pages/OAuthCallbackPage';
import PeriodsPage from './pages/PeriodsPage';
import PeriodReconPage from './pages/PeriodReconPage';

// Components
import Layout from './components/Layout';

// Protected Route Component
const ProtectedRoute = ({ children, adminOnly = false }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FBFBF9]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#006241]"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (adminOnly && user.role !== 'admin') {
    return <Navigate to="/" replace />;
  }

  return <Layout>{children}</Layout>;
};

// Public Route Component (redirect if logged in)
const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FBFBF9]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#006241]"></div>
      </div>
    );
  }

  if (user) {
    return <Navigate to="/" replace />;
  }

  return children;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route
        path="/login"
        element={
          <PublicRoute>
            <LoginPage />
          </PublicRoute>
        }
      />

      {/* Protected Routes */}
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <ShiftsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/cashup/:shiftId"
        element={
          <ProtectedRoute>
            <CashupPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/ledger"
        element={
          <ProtectedRoute>
            <LedgerPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/expenses"
        element={
          <ProtectedRoute>
            <ExpensesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/cash-count"
        element={
          <ProtectedRoute>
            <CashCountPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/export"
        element={
          <ProtectedRoute>
            <ExportPage />
          </ProtectedRoute>
        }
      />

      {/* Admin Routes */}
      <Route
        path="/users"
        element={
          <ProtectedRoute adminOnly>
            <UsersPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/api-keys"
        element={
          <ProtectedRoute adminOnly>
            <ApiKeysPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/api-users"
        element={
          <ProtectedRoute adminOnly>
            <APIUsersPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/accounting-settings"
        element={
          <ProtectedRoute adminOnly>
            <AccountingSettingsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/periods"
        element={
          <ProtectedRoute adminOnly>
            <PeriodsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/periods/:periodId"
        element={
          <ProtectedRoute adminOnly>
            <PeriodReconPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/oauth/callback"
        element={<OAuthCallbackPage />}
      />
      <Route
        path="/shift-search"
        element={
          <ProtectedRoute>
            <ShiftSearchPage />
          </ProtectedRoute>
        }
      />

      {/* Catch all - redirect to home */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
        <Toaster 
          position="top-right" 
          richColors 
          closeButton
          toastOptions={{
            style: {
              fontFamily: 'Inter, sans-serif'
            }
          }}
        />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
